import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../config/editor-environment';

export interface CustomMaterial {
  id: string;
  name: string;
  description?: string;
  density: number;
  youngModulus: number;
  poissonsRatio: number;
  yieldStress: number;
  thermalExpansion: number;
  thermalConductivity: number;
  referenceTemperature: number;
  category: string;
  type: string;
  manufacturingMethod: string;
  pricePerKg?: number;
  is_system_preset: boolean;
  is_owner: boolean;
  can_edit: boolean;
  can_delete: boolean;
  owner?: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
  };
  created_at: Date;
  updated_at: Date;
}

export interface MaterialListResponse {
  status: string;
  data: CustomMaterial[];
}

export interface MaterialResponse {
  status: string;
  message?: string;
  data: CustomMaterial;
}

export interface MaterialFilters {
  scope?: 'own' | 'organization' | 'all';
  category?: string;
  type?: string;
  manufacturingMethod?: string;
  search?: string;
}

export interface CreateMaterialDto {
  name: string;
  description?: string;
  density: number;
  youngModulus: number;
  poissonsRatio: number;
  yieldStress: number;
  thermalExpansion: number;
  thermalConductivity: number;
  referenceTemperature: number;
  category: string;
  type: string;
  manufacturingMethod: string;
  pricePerKg?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MaterialApiService {
  private http = inject(HttpClient);
  private readonly isDesktop = !!(window as any).electronAPI;

  /**
   * Get all custom materials accessible to the user
   */
  async getCustomMaterials(filters: MaterialFilters = {}): Promise<MaterialListResponse> {
    if (this.isDesktop) {
      const materials = await (window as any).electronAPI.materialFind({
        search: filters.search,
        category: filters.category,
        type: filters.type,
        manufacturingMethod: filters.manufacturingMethod
      });
      return {
        status: 'ok',
        data: (materials || []).map(addDesktopPermissions)
      };
    }

    const params = new URLSearchParams();

    if (filters.scope) params.append('scope', filters.scope);
    if (filters.category) params.append('category', filters.category);
    if (filters.type) params.append('type', filters.type);
    if (filters.manufacturingMethod) params.append('manufacturingMethod', filters.manufacturingMethod);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `${environment.API_ADDRESS}/materials${queryString ? '?' + queryString : ''}`;

    return lastValueFrom(this.http.get<MaterialListResponse>(url));
  }

  /**
   * Get a single custom material by ID
   */
  async getMaterial(id: string): Promise<MaterialResponse> {
    if (this.isDesktop) {
      const material = await (window as any).electronAPI.materialFindOne(id);
      return { status: 'ok', data: addDesktopPermissions(material) };
    }

    const url = `${environment.API_ADDRESS}/materials/${id}`;
    return lastValueFrom(this.http.get<MaterialResponse>(url));
  }

  /**
   * Create a new custom material
   */
  async createMaterial(material: CreateMaterialDto): Promise<MaterialResponse> {
    if (this.isDesktop) {
      const created = await (window as any).electronAPI.materialCreate(material);
      return { status: 'ok', data: addDesktopPermissions(created) };
    }

    const url = `${environment.API_ADDRESS}/materials`;
    return lastValueFrom(this.http.post<MaterialResponse>(url, material));
  }

  /**
   * Update an existing custom material
   */
  async updateMaterial(id: string, material: Partial<CreateMaterialDto>): Promise<MaterialResponse> {
    if (this.isDesktop) {
      const updated = await (window as any).electronAPI.materialUpdate(id, material);
      return { status: 'ok', data: addDesktopPermissions(updated) };
    }

    const url = `${environment.API_ADDRESS}/materials/${id}`;
    return lastValueFrom(this.http.patch<MaterialResponse>(url, material));
  }

  /**
   * Delete a custom material
   */
  async deleteMaterial(id: string): Promise<{ message: string }> {
    if (this.isDesktop) {
      await (window as any).electronAPI.materialDelete(id);
      return { message: 'Material deleted' };
    }

    const url = `${environment.API_ADDRESS}/materials/${id}`;
    return lastValueFrom(this.http.delete<{ message: string }>(url));
  }

  /**
   * Duplicate a material to user's own collection
   */
  async duplicateMaterial(id: string): Promise<MaterialResponse> {
    if (this.isDesktop) {
      const duplicated = await (window as any).electronAPI.materialDuplicate(id);
      return { status: 'ok', data: addDesktopPermissions(duplicated) };
    }

    const url = `${environment.API_ADDRESS}/materials/${id}/duplicate`;
    return lastValueFrom(this.http.post<MaterialResponse>(url, {}));
  }
}

/** On desktop all materials belong to the local user — grant full permissions. */
function addDesktopPermissions(material: CustomMaterial): CustomMaterial {
  return {
    ...material,
    is_system_preset: material.is_system_preset ?? false,
    is_owner: true,
    can_edit: true,
    can_delete: true,
  };
}
